import { useId, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { without } from '@/components/catalog/form-utils';
import { ApiError, api } from '@/lib/api';
import type {
  B2bServiceMap,
  B2bServiceMapCreateInput,
  B2bServiceMapUpdateInput,
  Product,
} from '@/lib/types';
import { Button, Checkbox, Combobox, Field, Input, Modal } from '@/components/ui';
import {
  KNOWN_SERVICE_KEYS,
  RETIRE_CONSEQUENCE,
  productSubtitle,
  serviceKeyIsEnterable,
} from './b2b-service-map-rules';
import { useB2bServiceProducts } from './use-b2b-service-products';

/*
 * Create or edit one service mapping.
 *
 * 🔴 **EVERY DECISION IN THIS FILE IS ABOUT ONE FAILURE: A MAPPING THAT POINTS
 * AT THE WRONG PRODUCT.** It is accepted cleanly, and afterwards nothing
 * disagrees with it — a proposal's service resolves to a real product name,
 * beside a real service key, on every screen, for ever. The raw key is kept
 * verbatim and joined to only through this table, so there is no second artefact
 * anywhere that could contradict a wrong row. Nothing downstream can catch this,
 * which is why the whole of the defence is here, at the moment of entering.
 *
 * Three things follow from that and none is decoration:
 *
 *  1. **The catalogue is COMPLETE or the form says so.** A product missing
 *     because it fell past a page cut-off is indistinguishable from one that does
 *     not exist, and the person who cannot find it picks the plausible neighbour.
 *  2. **The chosen product's CODE is rendered under the picker**, because the
 *     trigger shows the label alone and truncates it — and these names share long
 *     prefixes and differ at the tail, so on a chosen row the discriminator is
 *     exactly what falls off the end.
 *  3. **Nothing is pre-selected, ever, and there is no "none" row.** See the
 *     `Combobox` below; it is the sharpest rule on this screen.
 */

export interface B2bServiceMapFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Absent on create. */
  row?: B2bServiceMap;
  /** Created or updated. The caller re-reads; nothing is patched in place. */
  onSaved: () => void;
}

/**
 * The form's own value object.
 *
 * ⚠️ **`productId` is a string because a picker's value is**, and it becomes a
 * number inside the payload builders at the foot of this file and nowhere else.
 */
interface FormValues {
  serviceKey: string;
  productId: string;
  isActive: boolean;
}

type ValueKey = keyof FormValues;

/**
 * The WIRE keys a 422 may name.
 *
 * ⚠️ **These are the server's spellings, and here they happen to match the
 * controls** — which is a coincidence rather than a rule, and the reason the map
 * below exists anyway. `routeFieldErrors` partitions on what the SERVER said: a
 * list written from the control names would route every real refusal into the
 * banner, marked against nothing, while still counting toward "fields need
 * attention below".
 */
const WIRE_KEYS: readonly string[] = ['serviceKey', 'productId', 'isActive'];

/** Wire key → the control that renders it. */
const CONTROL_FOR_WIRE_KEY: Record<string, ValueKey> = {
  serviceKey: 'serviceKey',
  productId: 'productId',
  isActive: 'isActive',
};

export function B2bServiceMapFormModal({
  open,
  onClose,
  row,
  onSaved,
}: B2bServiceMapFormModalProps): ReactElement | null {
  if (!open) return null;
  /*
   * Remounted per record, so a draft opened on one mapping cannot carry into the
   * next.
   */
  return <B2bServiceMapForm key={row?.id ?? 'new'} onClose={onClose} row={row} onSaved={onSaved} />;
}

function B2bServiceMapForm({
  onClose,
  row,
  onSaved,
}: {
  onClose: () => void;
  row?: B2bServiceMap;
  onSaved: () => void;
}) {
  const formId = useId();
  /*
   * ⚠️ **The suggestion list's DOM id, from `useId()` and never a literal.** What
   * a duplicate id costs is at the `<datalist>` on the key field below.
   */
  const serviceKeysListId = useId();
  const isEdit = row !== undefined;

  const baseline = useMemo<FormValues>(() => {
    if (!row) return { serviceKey: '', productId: '', isActive: true };
    return {
      serviceKey: row.serviceKey,
      productId: String(row.product.id),
      isActive: row.isActive,
    };
  }, [row]);

  const [values, setValues] = useState<FormValues>(baseline);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  const products = useB2bServiceProducts();

  const changed = useMemo<ValueKey[]>(() => {
    const keys = Object.keys(baseline) as ValueKey[];
    return keys.filter((field) => {
      const next = values[field];
      const previous = baseline[field];
      return typeof next === 'string' && typeof previous === 'string'
        ? next.trim() !== previous.trim()
        : next !== previous;
    });
  }, [values, baseline]);
  const isDirty = changed.length > 0;

  function setField<K extends ValueKey>(field: K, next: FormValues[K]) {
    setValues((prev) => ({ ...prev, [field]: next }));
    setClientErrors((prev) => (field in prev ? without(prev, field) : prev));
    setServerErrors((prev) => (field in prev ? without(prev, field) : prev));
  }

  /**
   * The chosen product in full.
   *
   * ⚠️ **THREE different facts arrive here as `null`** — the catalogue is still
   * arriving, the catalogue read FAILED, and a value the loaded catalogue does
   * not contain. {@link ChosenProduct} tells all three apart rather than
   * printing one sentence for them; the middle one is the dangerous member,
   * because {@link useB2bServiceProducts} EMPTIES the list on a failed read, so
   * an unread catalogue and a deleted product are byte-identical here.
   */
  const chosen = useMemo(() => {
    const id = Number(values.productId);
    return Number.isInteger(id) ? (products.byId.get(id) ?? null) : null;
  }, [products.byId, values.productId]);

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (!serviceKeyIsEnterable(values.serviceKey)) {
      next.serviceKey = 'Enter the key exactly as the B2B site sends it.';
    }
    if (values.productId === '') next.productId = 'Choose the product this service means.';
    return next;
  }

  /**
   * Whether an edit has anything to send.
   *
   * ⚠️ **Derived from the PATCH the builder would produce, not from `changed`.**
   * They agree today; deriving it from the real body is what keeps Save honest if
   * the builder ever stops emitting a key — an empty PATCH is a 422 and a refusal
   * for "you changed nothing" is a worse answer than a button never offered.
   */
  const patchSize = isEdit ? Object.keys(buildPatch(values, changed)).length : 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;

    const clientSide = validate();
    setClientErrors(clientSide);
    if (Object.keys(clientSide).length > 0) {
      setFormError(null);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    setServerErrors({});
    try {
      if (row) await api.updateB2bServiceMap(row.id, buildPatch(values, changed));
      else await api.createB2bServiceMap(buildCreate(values));
      onSaved();
      onClose();
    } catch (caught) {
      applyFailure(caught);
    } finally {
      setSubmitting(false);
    }
  }

  function applyFailure(caught: unknown) {
    if (!(caught instanceof ApiError)) {
      setFormError('Could not reach the server. Nothing has been saved.');
      return;
    }

    /*
     * ⚠️ **A duplicate key is a 409 and carries NO `details`**, so nothing routes
     * it on its own. It is put on the key field by hand because that is the only
     * field it can be about, and it is the ordinary way this call fails: entering
     * nine keys, somebody enters one twice because nothing on the form tells them
     * they already did.
     *
     * ⚠️ **AND THE SUGGESTION LIST ON THE KEY FIELD DOES NOT CHANGE THAT — it is
     * the first thing that looks as though it might.** `KNOWN_SERVICE_KEYS`
     * offers the keys that EXIST AT THE SENDER, not the keys already mapped in
     * this table, and it goes on offering one after it has been used. So a key
     * taken straight off the list can still be the one somebody entered an hour
     * ago. **Nothing on this form knows what is already in the table**, and this
     * 409 is still the first artefact that says so.
     *
     * ⛔ **AND IT CAN FIRE ON A KEY THAT LOOKS DIFFERENT.** `service_key` carries
     * a UNIQUE index over a `utf8mb4_unicode_ci` column, so `AI_VISA` collides
     * with `ai_visa`. **Nothing here folds case to pre-empt that** — the
     * vocabulary is the sender's — so the server's own sentence is rendered
     * rather than one invented here, which is also the only one that can name the
     * row already holding the key.
     */
    if (caught.status === 409) {
      setServerErrors({ serviceKey: caught.message });
      setFormError(null);
      return;
    }

    if (caught.status === 403) {
      setFormError(
        caught.message || 'Your account no longer has permission to change these mappings.',
      );
      return;
    }

    const routed = caught.routeFieldErrors(WIRE_KEYS);
    const fields: Record<string, string> = {};
    for (const [wireKey, message] of Object.entries(routed.fields)) {
      const control = CONTROL_FOR_WIRE_KEY[wireKey];
      if (control) fields[control] = message;
    }
    setServerErrors(fields);
    /*
     * `unroutable` is everything the server named that no control here renders.
     * It goes in the banner and is deliberately NOT counted toward "fields need
     * attention below", which would point at nothing.
     */
    const banner = [...routed.unroutable, ...caught.formMessages];
    setFormError(banner.length > 0 ? banner.join(' ') : (caught.formMessage ?? caught.message));
  }

  const fieldErrorCount = Object.keys(errors).length;
  /*
   * ⚠️ Save is offerable on create from the start, and on edit only once
   * something has moved. It is NOT disabled while the catalogue loads: the key
   * can be typed meanwhile, and the client-side check refuses an empty product.
   */
  const saveDisabled = submitting || (isEdit && patchSize === 0);

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit service mapping' : 'Map a service key'}
      size="md"
      dismissOnBackdrop={!isDirty}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Create mapping'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="grid gap-4">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <Field label="Service key from the B2B site" required error={errors.serviceKey}>
          {({ id, 'aria-describedby': describedBy }) => (
            <>
              <Input
                id={id}
                value={values.serviceKey}
                onChange={(e) => setField('serviceKey', e.target.value)}
                /*
                 * ⚠️ **Spell-check and autocapitalise OFF.** These are machine keys
                 * carrying hyphens and underscores, and a phone keyboard that
                 * capitalises the first letter produces a key nobody reads back.
                 */
                spellCheck={false}
                autoCapitalize="none"
                autoCorrect="off"
                /*
                 * ⚠️ **`autoComplete="off"` AND THE `<datalist>` BELOW ARE NOT IN
                 * CONFLICT, THOUGH THEY READ AS THOUGH THEY WERE — KEEP BOTH.** The
                 * attribute suppresses the BROWSER'S OWN saved-value history,
                 * whatever this person has typed into a field like this before, on
                 * any form anywhere; the list offers OUR vocabulary. Drop the
                 * attribute and a remembered typo is mixed into a list of machine
                 * keys with nothing telling the two apart; drop the list and that
                 * typo is the only suggestion there is.
                 */
                autoComplete="off"
                list={serviceKeysListId}
                invalid={Boolean(errors.serviceKey)}
                aria-describedby={describedBy}
              />
              {/*
                * 🔴 **A `<datalist>`, AND DELIBERATELY NOT A `Combobox`. THE NEXT
                * READER WILL REACH FOR ONE, SO THE REASON IS HERE RATHER THAN IN A
                * COMMIT MESSAGE.** `src/components/ui/Combobox.tsx` says of itself
                * (lines 149-160) that it is **not** an editable combobox, because
                * "an input that both displays a selection and accepts free text is
                * the pattern users most often end up fighting" — so it emits only
                * values that ARE options. A key typed here that the list has not
                * caught up with could then not be submitted AT ALL, which is
                * precisely what `serviceKeyIsEnterable`'s own doc forbids: refusing
                * a key that is correct at the sender's end, from a list the sender
                * has never seen. **A `<datalist>` is a different control for a
                * different job — a text field WITH SUGGESTIONS, never a select** —
                * so anything typed still goes through, and `KNOWN_SERVICE_KEYS`
                * stays a suggestion list that nothing branches on.
                *
                * ⚠️ **THE FIRST `<datalist>` IN THIS CODEBASE** — `grep -rn
                * "datalist" src/` returned nothing before this. A deliberate first
                * rather than a stray pattern: this is the only field in the app
                * whose vocabulary belongs to ANOTHER REPOSITORY and must still
                * accept whatever it is given.
                *
                * ⚠️ **THE ID IS `useId()`'s AND MUST NEVER BECOME A LITERAL.** Two
                * mounts of this modal would emit two elements sharing one DOM id,
                * and an input binds to whichever the document reaches first — the
                * wrong list, silently, with both fields looking entirely correct.
                */}
              <datalist id={serviceKeysListId}>
                {KNOWN_SERVICE_KEYS.map((serviceKey) => (
                  <option key={serviceKey} value={serviceKey} />
                ))}
              </datalist>
            </>
          )}
        </Field>

        <div className="grid gap-1.5">
          <Field label="SEED EMERGE product" required error={errors.productId}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                value={values.productId}
                onChange={(next) => setField('productId', next)}
                options={products.options}
                loading={products.loading}
                placeholder="Choose the product this service means…"
                searchPlaceholder="Search products…"
                invalid={Boolean(errors.productId)}
                /*
                 * ⛔ **NO `emptyOptionLabel`, AND THIS IS THE SHARPEST RULE ON
                 * THE SCREEN.** A mapping naming nothing is a row that looks
                 * configured in the table — a key, a status, an Edit button —
                 * while resolving every proposal that names it to nothing at
                 * all. It is strictly worse than no row: an unmapped key is
                 * visibly absent from this list, where a mapping pointing at
                 * "none" is visibly present and silently inert. So "none" is not
                 * a choice this form may offer. Clearing a mapping is Delete.
                 */
                aria-describedby={describedBy}
              />
            )}
          </Field>

          <ChosenProduct
            chosen={chosen}
            loading={products.loading}
            lookupFailed={products.error !== null}
            hasValue={values.productId !== ''}
          />

          {products.error && (
            <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
              The product catalogue could not be read, so there is nothing to map this key to.{' '}
              {products.error}
            </p>
          )}
        </div>

        {/*
          * ⛔ **THE `description` STATES A FACT THE CONTROL CANNOT, AND A SWEEP
          * MUST NOT TAKE IT.** A tickbox whose effect is nil looks exactly like a
          * tickbox that closes a door, and a reader who unticks this and is told
          * nothing will believe they have retired a service. See
          * `RETIRE_CONSEQUENCE`, which also records why the `/event-maps` wording
          * — "the form refuses submissions naming this key" — is false here.
          */}
        <Checkbox
          checked={values.isActive}
          onChange={(e) => setField('isActive', e.target.checked)}
          label="Offer this mapping"
          description={RETIRE_CONSEQUENCE}
        />
      </form>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* The payloads — the ONE place a control becomes a wire key                   */
/* -------------------------------------------------------------------------- */

/**
 * ⚠️ **`isActive` is sent on CREATE only when it is not the server's default**, so
 * an ordinary new mapping carries the two keys it is actually about.
 */
function buildCreate(values: FormValues): B2bServiceMapCreateInput {
  return {
    serviceKey: values.serviceKey.trim(),
    productId: Number(values.productId),
    ...(values.isActive === false ? { isActive: false } : {}),
  };
}

/**
 * ⛔ **ONLY THE KEYS THAT MOVED.** A PATCH built from a spread of the loaded row
 * would re-assert `serviceKey` on a visit that merely retired the mapping — and
 * on a table whose keys are unique, re-sending one is the shape that collides
 * with itself.
 */
function buildPatch(values: FormValues, changed: readonly ValueKey[]): B2bServiceMapUpdateInput {
  const patch: B2bServiceMapUpdateInput = {};
  if (changed.includes('serviceKey')) patch.serviceKey = values.serviceKey.trim();
  if (changed.includes('productId')) patch.productId = Number(values.productId);
  if (changed.includes('isActive')) patch.isActive = values.isActive;
  return patch;
}

/* -------------------------------------------------------------------------- */
/* The chosen product, rendered in full                                        */
/* -------------------------------------------------------------------------- */

/**
 * What the picker's trigger cannot show.
 *
 * 🔴 **THIS IS THE GUARD AGAINST A WRONG MATCH, NOT A CAPTION ABOUT ONE.** The
 * trigger renders `label` alone and truncates it, and these product names share
 * long prefixes and differ at the tail — so the code, the add-on marker and the
 * retirement are exactly what falls off the end. This renders the VALUE, the
 * same standing as a `title` carrying what a cell truncated.
 *
 * ⚠️ **FOUR states, and only ONE of them is "nothing chosen".** Nothing chosen,
 * still arriving, the catalogue read FAILED, and a value the loaded catalogue
 * genuinely does not contain. A value the loaded catalogue does not contain must
 * not render as a blank that reads like an empty picker.
 *
 * 🔴 **THE MIDDLE TWO BOTH LEAVE `chosen` NULL AND MEAN OPPOSITE THINGS, WHICH
 * IS WHY `lookupFailed` IS A PARAMETER RATHER THAN A REFINEMENT.**
 * `useB2bServiceProducts` EMPTIES the list on a failed read — deliberately, so
 * nothing can be saved against a list nobody can vouch for — so an unread
 * catalogue and a deleted product are byte-identical by the time they reach
 * here. Without the flag this accuses a live mapping of pointing at a product
 * that does not exist **whenever the catalogue could not be read**, in a
 * `role="alert"` that is announced, and instructs the reader to choose again
 * from a picker that is empty for the very same reason. The plausible next
 * action is to delete the row and re-enter it, which costs every proposal
 * naming that key.
 *
 * ⛔ **THE SURVIVING ACCUSATION IS UNREACHABLE WHILE THE FK IS `ON DELETE
 * RESTRICT`** (migration 231, guard 4): a mapped product provably still exists,
 * and the catalogue is read unfiltered so it is provably in the list. It is kept
 * as the honest answer should that invariant ever be relaxed — **not** as a
 * branch anybody should expect to meet, and not as a state to write copy about
 * elsewhere.
 */
function ChosenProduct({
  chosen,
  loading,
  lookupFailed,
  hasValue,
}: {
  chosen: Product | null;
  loading: boolean;
  lookupFailed: boolean;
  hasValue: boolean;
}) {
  if (!hasValue) return null;

  if (chosen === null) {
    if (loading) {
      return <p className="m-0 text-xs text-fg-muted">Looking up what this key points at…</p>;
    }
    /*
     * ⚠️ **A read that did not happen is not evidence about a product.** The
     * catalogue's own failure notice renders directly below this and carries the
     * fact, so the only honest output here is nothing — the same ruling
     * `/event-maps`' `TargetSubtitle` already takes for its panel.
     */
    if (lookupFailed) return null;
    return (
      <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
        This mapping points at a product that is not in the catalogue. Choose it again before
        saving.
      </p>
    );
  }

  const subtitle = productSubtitle(chosen);

  return (
    <div className="rounded-md border border-border bg-surface-sunken px-3 py-2">
      <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
        {chosen.productName}
      </p>
      {/*
        * ⚠️ **Says so plainly when a product carries no code and is not an
        * add-on.** That is a fact about the catalogue a person needs before
        * mapping a service to it — not something to hide behind a blank that
        * reads as "still loading".
        */}
      <p className="mt-0.5 mb-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
        {subtitle || (
          <span className="text-fg-subtle">Nothing recorded to tell it apart from another</span>
        )}
      </p>
      {!chosen.productActive && (
        <p className="mt-1 mb-0 text-xs text-warning">
          This product is retired. Proposals naming this service would still resolve to it.
        </p>
      )}
    </div>
  );
}

/*
 * ⚠️ **The catalogue lives in `use-b2b-service-products.ts`, not here.** The
 * TABLE needs the same records for the same reason this form does — a mapping row
 * carries only `{id, name}` — so one hook serves both and a product reads the
 * same in the picker as it does in the row that picker produced.
 */
